import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
} from "react";

import { useDisclosure } from "@UI/hooks/useDisclosure";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";

import { TaskCreationResumeModal } from "../Components/TaskCreationResumeModal/TaskCreationResumeModal";
import { useTaskCreationSummary } from "../hooks/useTaskCreationSummary";
import messages from "../messages";

export interface IDiscardConfirmationContext {
  /** Opens the leave-guard modal; `onConfirm` only runs if the visitor picks
   * "Discard and start over". */
  requestDiscard: (onConfirm: () => void) => void;
}

// Default: no provider mounted means no guard, not a crash — several existing
// unit tests render `DynamicScreen`/`SubcategoryScreen` (which call
// `useFlowBack`) standalone, mocking the flow context wholesale without this
// provider. Production always mounts it via `TaskCreationShell`.
//
// It drops the callback rather than running it: a forgotten provider must not
// silently discard someone's draft, which is what confirming does.
const DiscardConfirmationContext = createContext<IDiscardConfirmationContext>({
  requestDiscard: () => undefined,
});

export const useDiscardConfirmation = (): IDiscardConfirmationContext =>
  useContext(DiscardConfirmationContext);

/**
 * Owns the "go back and start over?" guard on the description-screen branch
 * of `useFlowBack` (RT-4321): that hook calls `requestDiscard` instead of
 * navigating straight away, and only a confirmed discard runs its navigation.
 *
 * Mounted once around the whole flow (`TaskCreationShell`, inside the flow
 * provider) so its state survives whichever screen is on show. Reuses
 * `TaskCreationResumeModal` with its own copy rather than forking it.
 */
export const DiscardConfirmationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { category, steps } = useTaskCreationSummary();
  // The confirm callback varies per call (it closes over the caller's
  // destination), so it cannot live in the context value itself.
  const onConfirmRef = useRef<() => void>();

  const requestDiscard = useCallback(
    (onConfirm: () => void) => {
      onConfirmRef.current = onConfirm;
      onOpen();
    },
    [onOpen]
  );

  const handleDiscard = () => {
    onClose();
    onConfirmRef.current?.();
  };

  return (
    <DiscardConfirmationContext.Provider value={{ requestDiscard }}>
      {children}
      <TaskCreationResumeModal
        isOpen={isOpen}
        onClose={onClose}
        onContinue={onClose}
        onDiscard={handleDiscard}
        category={category}
        steps={steps}
        title={
          <FormattedMessage {...messages["taskCreation.discardGuard.title"]} />
        }
        body={
          <FormattedMessage {...messages["taskCreation.discardGuard.body"]} />
        }
        continueLabel={
          <FormattedMessage {...messages["taskCreation.discardGuard.cancel"]} />
        }
        discardLabel={
          <FormattedMessage
            {...messages["taskCreation.discardGuard.confirm"]}
          />
        }
      />
    </DiscardConfirmationContext.Provider>
  );
};
